import { useMemo } from "react";
import type { StoreEvent } from "../../../redux/events/events-types";

export interface PlaylistItem {
	url: string;
	teamId?: number | null;
}

const PROMO_VIDEOS = [
	"/heavy/media/event-kiosk/Logo-4-[remix]-EN.mp4",
	"/heavy/media/event-kiosk/inJOY- 0301-0.mp4",
	"/heavy/media/event-kiosk/inJOY- 0301-1.mp4",
	"/heavy/media/event-kiosk/inJOY- 0301-2.mp4",
];

const shuffleArray = <T>(array: T[]): T[] => {
	const newArray = [...array];
	for (let i = newArray.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[newArray[i], newArray[j]] = [newArray[j], newArray[i]];
	}
	return newArray;
};

/** Collect all highlight video URLs from event sessions (up to but not including `upToIdx`). */
const collectHighlights = (
	event: StoreEvent | null,
	upToIdx?: number,
): PlaylistItem[] => {
	if (!event?.sessions) return [];
	const limit = upToIdx ?? event.sessions.length;
	const items: PlaylistItem[] = [];
	for (let i = 0; i < limit; i++) {
		const session = event.sessions[i];
		if (session.highlights) {
			for (const h of session.highlights) {
				if (h.video_url)
					items.push({ url: h.video_url, teamId: (h as any).team_id ?? null });
			}
		}
	}
	return items;
};

export const useKioskPlaylist = (
	displayStage: string,
	displaySubStage: string | null,
	activeSessionIdx: number,
	event: StoreEvent | null,
): PlaylistItem[] => {
	const newPlaylist = useMemo(() => {
		let playlist: PlaylistItem[] = [];

		if (displayStage === "idle") {
			playlist = collectHighlights(event);
			if (playlist.length === 0) {
				playlist = PROMO_VIDEOS.map((url) => ({ url }));
			}
		} else if (displayStage === "waiting") {
			playlist = collectHighlights(event, activeSessionIdx);
			if (playlist.length === 0) {
				playlist = PROMO_VIDEOS.map((url) => ({ url }));
			}
		} else if (displayStage === "playing") {
			const sessionStreamUrl = (event?.sessions?.[activeSessionIdx] as any)
				?.stream_url;
			const streamUrl =
				sessionStreamUrl || event?.control?.stream_status?.preview_url;
			playlist = [{ url: streamUrl || "LIVE_FEED_PLACEHOLDER" }];
		} else if (displayStage === "endgame") {
			if (displaySubStage === "processing") {
				const currentReplays: PlaylistItem[] = [];
				if (event?.sessions?.[activeSessionIdx]?.result_details) {
					for (const res of event.sessions[activeSessionIdx].result_details ??
						[]) {
						if (res.replay_url) {
							currentReplays.push({
								url: res.replay_url,
								teamId: (res as any).team_id ?? null,
							});
						}
					}
				}
				playlist = shuffleArray(currentReplays);
			} else {
				if (event?.sessions?.[activeSessionIdx]?.highlights) {
					playlist = event.sessions[activeSessionIdx].highlights
						.filter((h) => !!h.video_url)
						.map((h) => ({
							url: h.video_url as string,
							teamId: (h as any).team_id ?? null,
						}));
				}

				// If highlights aren't ready for THIS game, fall back to THIS game's replays
				if (playlist.length === 0) {
					const currentReplays: PlaylistItem[] = [];
					if (event?.sessions?.[activeSessionIdx]?.result_details) {
						for (const res of event.sessions[activeSessionIdx].result_details ??
							[]) {
							if (res.replay_url) {
								currentReplays.push({
									url: res.replay_url,
									teamId: (res as any).team_id ?? null,
								});
							}
						}
					}
					playlist = shuffleArray(currentReplays);
				}
			}

			// If absolutely no replays or highlights for THIS game, only then use promo videos.
			if (playlist.length === 0) {
				playlist = PROMO_VIDEOS.map((url) => ({ url }));
			}
		}
		return playlist;
	}, [
		displayStage,
		displaySubStage,
		activeSessionIdx,
		event?.sessions,
		event?.control?.stream_status?.preview_url,
		event,
	]);

	return newPlaylist;
};
